import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import LanguageContext from './LanguageContext';
import { profilePageCopy } from './translations';
import './Profile.css';

function Profile() {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const copy = profilePageCopy[language] || profilePageCopy.English;
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    userId: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const userLoggedIn = localStorage.getItem('userLoggedIn');
    if (userLoggedIn !== 'true') {
      navigate('/login');
      return;
    }

    // Get user data from localStorage
    const userId = localStorage.getItem('userId');
    const userEmail = localStorage.getItem('userEmail');
    const userName = localStorage.getItem('userName');

    if (userId && userEmail) {
      setUserData({
        name: userName || 'User',
        email: userEmail,
        userId: userId
      });
      
      // Optionally fetch additional data from Supabase
      fetchUserDetails(userId);
    } else {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchUserDetails = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .eq('is_deleted', false) // Only fetch non-deleted users
        .single();

      if (error) {
        console.error('Error fetching user details:', error);
        // If user is deleted or not found, logout
        if (error.code === 'PGRST116') {
          handleLogout();
          return;
        }
      } else if (data) {
        // Check if user is deleted
        if (data.is_deleted) {
          handleLogout();
          return;
        }
        setUserData({
          name: data.full_name || data.name || data.email?.split('@')[0] || 'User',
          email: data.email || userData.email,
          userId: data.id
        });
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    window.dispatchEvent(new Event('userLogout'));
    navigate('/');
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);

    if (!userData.userId) {
      setDeleteFeedback(copy.deleteError);
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteFeedback('');

      // Mark user as deleted instead of actually deleting the record
      const { error } = await supabase
        .from('users')
        .update({ 
          is_deleted: true,
          deleted_at: new Date().toISOString()
        })
        .eq('id', userData.userId);

      if (error) {
        throw error;
      }

      // Clear local storage and logout
      localStorage.removeItem('userLoggedIn');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      window.dispatchEvent(new Event('userLogout'));

      setDeleteFeedback(copy.deleteSuccess);

      setTimeout(() => {
        navigate('/');
      }, 2500);
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteFeedback(err.message || copy.deleteFailed);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="loading-spinner-container">
            <div className="loading-spinner"></div>
            <p>{copy.loadingProfile}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <h1 className="profile-title">{copy.pageTitle}</h1>
        </div>

        <div className="profile-content">
          <div className="profile-card">
            <div className="profile-info-section">
              <div className="info-item">
                <label className="info-label">{copy.nameLabel}</label>
                <div className="info-value">{userData.name}</div>
              </div>
              <div className="info-item">
                <label className="info-label">{copy.emailLabel}</label>
                <div className="info-value">{userData.email}</div>
              </div>
            </div>
            <div className="profile-actions">
              <button className="btn-primary" onClick={handleLogout}>
                {copy.logoutButton}
              </button>
              <button
                className="btn-delete"
                onClick={handleDeleteClick}
                disabled={isDeleting}
              >
                {isDeleting ? copy.deleting : copy.deleteAccountButton}
              </button>
            </div>
            {deleteFeedback && (
              <p className="delete-feedback">{deleteFeedback}</p>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrapper">
              <svg className="modal-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="modal-title">{copy.deleteModalTitle}</h2>
            <p className="modal-message">
              {copy.deleteModalMessage}
              <br /><br />
              <strong>{copy.deleteModalWarning}</strong>
            </p>
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={handleCancelDelete}>
                {copy.cancelButton}
              </button>
              <button className="modal-btn-confirm" onClick={handleConfirmDelete}>
                {copy.confirmDeleteButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="container">
          <div className="footer-content" style={{ textAlign: 'center' }}>
            <div className="footer-column">
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>Address: 1st Floor, 124 Cleveland Street, London W1T 6PG</p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="mailto:manawave@manawave.uk" style={{ color: '#C7D2FE', textDecoration: 'none' }}>manawave@manawave.uk</a>
              </p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="tel:+447356083797" style={{ color: '#C7D2FE', textDecoration: 'none' }}>+447356083797</a>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>Copyright © 2026 ManaWave LTD All Rights Reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Profile;

